import {
	json,
	type ActionFunction,
	type LoaderFunction,
	redirect,
} from '@remix-run/node'
import { useLoaderData, useFetcher } from '@remix-run/react'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { Icon } from '#app/components/ui/icon.tsx'
import { getUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.js'
import FundingProfilePage from './fundingProfiles+/FundingProfilePage'
import { type GrantSearchFilterValues } from './grants+/GrantSearchFilter'

// Define the type for our subscription data
type Subscription = {
	id: string
	name: string
	isActive: boolean
	territories: { grantTerritory: { id: string; name: string } }[]
	fundingAreas: { grantFundingArea: { id: string; name: string } }[]
	fundingTypes: { grantFundingType: { id: string; name: string } }[]
	grantNameFilter: string | null
}

interface FilterOption {
	id: string
	name: string
}

// Define the loader data type
interface LoaderData {
	subscriptions: Subscription[]
	filterOptions: {
		fundingAreas: FilterOption[]
		territories: FilterOption[]
		fundingTypes: FilterOption[]
	}
	isAuthenticated: boolean
	isFirstTime: boolean
	userEmail?: string
}

// Define action data types
type ActionData =
	| { action: 'create'; data: GrantSearchFilterValues }
	| { action: 'updateStatus'; subscriptionId: string; isActive: boolean }
	| { action: 'delete'; subscriptionIds: string[] }

// Example subscription data for unauthenticated users
const exampleSubscriptions = [
	{
		id: 'example-1',
		name: 'Healthcare Research Profile',
		territories: 'Canada, United States',
		fundingAreas: 'Health, Medical Research',
		fundingTypes: 'Research Grants, Innovation Funding',
		isActive: true,
	},
	{
		id: 'example-2',
		name: 'Environmental Sustainability',
		territories: 'Global',
		fundingAreas: 'Environment, Climate Change',
		fundingTypes: 'Project Grants, Capacity Building',
		isActive: true,
	},
	{
		id: 'example-3',
		name: 'Technology Innovation',
		territories: 'North America, Europe',
		fundingAreas: 'Technology, Innovation',
		fundingTypes: 'Startup Funding, R&D Grants',
		isActive: false,
	},
]

export const loader: LoaderFunction = async ({ request }) => {
	const userId = await getUserId(request)

	// Get filter options from database (only those with associated grants)
	const territories = await prisma.grantTerritory.findMany({
		distinct: ['name'],
		select: { id: true, name: true },
		where: {
			GrantTerritoryGrantRecord: {
				some: {},
			},
		},
	})
	const fundingAreas = await prisma.grantFundingArea.findMany({
		distinct: ['name'],
		select: { id: true, name: true },
		where: {
			GrantRecords: {
				some: {},
			},
		},
	})
	const fundingTypes = await prisma.grantFundingType.findMany({
		distinct: ['name'],
		select: { id: true, name: true },
		where: {
			GrantRecords: {
				some: {},
			},
		},
	})

	const filterOptions = {
		territories: territories
			.map(t => ({ id: t.id, name: t.name }))
			.sort((a, b) => a.name.localeCompare(b.name)),
		fundingAreas: fundingAreas
			.map(fa => ({ id: fa.id, name: fa.name }))
			.sort((a, b) => a.name.localeCompare(b.name)),
		fundingTypes: fundingTypes
			.map(ft => ({ id: ft.id, name: ft.name }))
			.sort((a, b) => a.name.localeCompare(b.name)),
	}

	// If user is not authenticated, return with example data
	if (!userId) {
		return json({
			subscriptions: [],
			filterOptions,
			isAuthenticated: false,
			isFirstTime: false,
		} as LoaderData)
	}

	// If user is authenticated, get their funding profile status and subscriptions
	const user = await prisma.user.findUnique({
		select: { fundingProfileCompleted: true, email: true },
		where: { id: userId },
	})

	// Get user's subscriptions
	const subscriptions = await prisma.grantSearchSubscription.findMany({
		where: { userId },
		include: {
			territories: { include: { grantTerritory: true } },
			fundingAreas: { include: { grantFundingArea: true } },
			fundingTypes: { include: { grantFundingType: true } },
		},
	})

	// Determine if this is a first-time user (no funding profile completed)
	const isFirstTime = !user?.fundingProfileCompleted

	return json({
		subscriptions,
		filterOptions,
		isAuthenticated: true,
		isFirstTime,
		userEmail: user?.email || '',
	} as LoaderData)
}

export const action: ActionFunction = async ({ request }) => {
	const userId = await getUserId(request)

	// Require authentication for actions
	if (!userId) {
		return redirect('/login')
	}

	const actionData = (await request.json()) as ActionData

	if (actionData.action === 'updateStatus') {
		// Update subscription status
		const subscription = await prisma.grantSearchSubscription.update({
			where: {
				id: actionData.subscriptionId,
				userId, // Ensure user owns this subscription
			},
			data: {
				isActive: actionData.isActive,
			},
		})
		return json({ subscription, action: 'updateStatus' })
	}

	if (actionData.action === 'delete') {
		// Delete subscriptions
		await prisma.grantSearchSubscription.deleteMany({
			where: {
				id: { in: actionData.subscriptionIds },
				userId, // Ensure user owns these subscriptions
			},
		})
		return json({ success: true })
	}

	// Handle subscription creation
	const formData = actionData.data

	// Handle multiple selections for territories
	const territoryConnections =
		formData.territories && formData.territories.length > 0
			? formData.territories.map(territoryId => ({
					grantTerritory: {
						connect: { id: territoryId },
					},
				}))
			: undefined

	// Handle multiple selections for funding areas
	const fundingAreaConnections =
		formData.fundingAreas && formData.fundingAreas.length > 0
			? formData.fundingAreas.map(fundingAreaId => ({
					grantFundingArea: {
						connect: { id: fundingAreaId },
					},
				}))
			: undefined

	// Handle multiple selections for funding types
	const fundingTypeConnections =
		formData.fundingTypes && formData.fundingTypes.length > 0
			? formData.fundingTypes.map(fundingTypeId => ({
					grantFundingType: {
						connect: { id: fundingTypeId },
					},
				}))
			: undefined

	const subscription = await prisma.grantSearchSubscription.create({
		data: {
			userId,
			name: '',
			territories: territoryConnections
				? {
						create: territoryConnections,
					}
				: undefined,
			fundingAreas: fundingAreaConnections
				? {
						create: fundingAreaConnections,
					}
				: undefined,
			fundingTypes: fundingTypeConnections
				? {
						create: fundingTypeConnections,
					}
				: undefined,
			grantNameFilter:
				formData.grantName === '' ? null : formData.grantName ?? null,
		},
	})

	// Mark funding profile as completed if this is their first one
	const existingSubscriptionsCount = await prisma.grantSearchSubscription.count(
		{
			where: { userId },
		},
	)

	if (existingSubscriptionsCount === 1) {
		await prisma.user.update({
			where: { id: userId },
			data: { fundingProfileCompleted: true },
		})
	}

	return json({ subscription, action: 'create' })
}

export default function FundingProfileRoute() {
	const { subscriptions, filterOptions, isAuthenticated, isFirstTime } =
		useLoaderData<LoaderData>()
	const fetcher = useFetcher<typeof action>()

	// Show success toast based on action type
	useEffect(() => {
		if (
			fetcher.state === 'idle' &&
			fetcher.data &&
			typeof fetcher.data === 'object' &&
			fetcher.data !== null
		) {
			if ('subscription' in fetcher.data && 'action' in fetcher.data) {
				if (fetcher.data.action === 'create') {
					toast.success('Funding profile created successfully!')
				} else if (fetcher.data.action === 'updateStatus') {
					toast.success('Subscription status updated successfully!')
				}
			} else if ('success' in fetcher.data && fetcher.data.success) {
				// This is a delete action
				toast.success('Subscription(s) deleted successfully!')
			}
		}
	}, [fetcher.state, fetcher.data])

	const handleCreateSubscription = (data: GrantSearchFilterValues) => {
		// If not authenticated, redirect to signup
		if (!isAuthenticated) {
			window.location.href = '/signup'
			return
		}

		if (
			!data.territories?.length &&
			!data.fundingAreas?.length &&
			!data.fundingTypes?.length
		) {
			toast.error(
				'Please fill in at least one filter field before creating a subscription',
			)
			return
		}

		const submitData = {
			action: 'create' as const,
			data: {
				territories: data.territories || [],
				fundingAreas: data.fundingAreas || [],
				fundingTypes: data.fundingTypes || [],
				grantName:
					data.grantName && data.grantName.trim() !== ''
						? data.grantName
						: null,
			},
		}

		fetcher.submit(submitData, { method: 'post', encType: 'application/json' })
	}

	// Transform subscriptions for the table component
	const transformedSubscriptions = isAuthenticated
		? subscriptions.map(sub => ({
				id: sub.id,
				name: sub.name,
				territories:
					sub.territories.map(t => t.grantTerritory.name).join(', ') || 'Any',
				fundingAreas:
					sub.fundingAreas.map(fa => fa.grantFundingArea.name).join(', ') ||
					'Any',
				fundingTypes:
					sub.fundingTypes.map(ft => ft.grantFundingType.name).join(', ') ||
					'Any',
				isActive: sub.isActive,
			}))
		: exampleSubscriptions

	// Handlers for authenticated users
	const handleActivate = (subscriptionId: string) => {
		if (!isAuthenticated) return

		const submitData = {
			action: 'updateStatus' as const,
			subscriptionId,
			isActive: true,
		}
		fetcher.submit(submitData, {
			method: 'post',
			encType: 'application/json',
		})
	}

	const handleDeactivate = (subscriptionId: string) => {
		if (!isAuthenticated) return

		const submitData = {
			action: 'updateStatus' as const,
			subscriptionId,
			isActive: false,
		}
		fetcher.submit(submitData, {
			method: 'post',
			encType: 'application/json',
		})
	}

	const handleDelete = (subscriptionIds: string[]) => {
		if (!isAuthenticated) return

		const submitData = {
			action: 'delete' as const,
			subscriptionIds,
		}
		fetcher.submit(submitData, {
			method: 'post',
			encType: 'application/json',
		})
	}

	// Show alert for unauthenticated users
	const alertContent = !isAuthenticated ? (
		<div className="mx-auto mb-8 max-w-4xl rounded-lg border border-brand-cyan-6 bg-brand-cyan-1 p-4">
			<div className="flex items-start gap-3">
				<Icon name="check" className="h-4 w-4 text-brand-cyan-8" />
				<div className="text-brand-cyan-11">
					<span className="font-semibold">Create your free account</span> to set
					up personalized funding profiles and get notified about grants that
					match your interests!
				</div>
			</div>
		</div>
	) : isFirstTime && subscriptions.length === 0 ? (
		<div className="mx-auto mb-8 max-w-4xl rounded-lg border border-brand-cyan-6 bg-brand-cyan-1 p-4">
			<div className="flex items-start gap-3">
				<Icon
					name="question-mark-circled"
					className="h-4 w-4 text-brand-cyan-8"
				/>
				<div className="text-brand-cyan-11">
					<span className="font-semibold">Welcome to DoGood Grants!</span> Let's
					set up your funding profile to help you discover grants that match
					your interests. This will only take a minute.
				</div>
			</div>
		</div>
	) : null

	return (
		<div className="container mx-auto px-4 py-8">
			{alertContent}

			<FundingProfilePage
				heroHeadlineProps={{}}
				profileFormCardProps={{
					filterOptions,
					onCreateSubscription: handleCreateSubscription,
					isSubmitting: fetcher.state !== 'idle',
					isPublicView: !isAuthenticated,
				}}
				featureListProps={{}}
				grantSubscriptionTableProps={{
					subscriptions: transformedSubscriptions,
					onActivate: isAuthenticated ? handleActivate : undefined,
					onDeactivate: isAuthenticated ? handleDeactivate : undefined,
					onDelete: isAuthenticated ? handleDelete : undefined,
					isSubmitting: fetcher.state !== 'idle',
					isExampleData: !isAuthenticated,
				}}
			/>
		</div>
	)
}
